import type React from 'react';
import { useId } from 'react';

import {
  ButtonGroup,
  FieldSet,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Select,
  ToggleButton,
} from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PARKING } from '../../../constants/index';
import CurrencyInput from '../../Widgets/CurrencyInput';

// Generate vehicle count options (1-5)
const vehicleCountOptions = Array.from({ length: 5 }, (_, i) => {
  const value = i + 1;
  return { value: String(value), label: `${value} vehicle${value === 1 ? '' : 's'}` };
});

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function Parking({ section, setSectionValues }: Props): JSX.Element {
  const radioGroupName = useId();
  const anyOpenSpotId = useId();
  const reservedSpaceId = useId();

  function onNoneClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.IS_NONE]: true,
      [PARKING.FIELD_NAME.IS_PROVIDED]: false,
    });
  }

  function onIncludedClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.IS_NONE]: false,
      [PARKING.FIELD_NAME.IS_PROVIDED]: true,
      [PARKING.FIELD_NAME.IS_INCLUDED]: true,
      [PARKING.FIELD_NAME.IS_ADDITIONAL_FEE]: false,
    });
  }

  function onAdditionalFeeClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.IS_NONE]: false,
      [PARKING.FIELD_NAME.IS_PROVIDED]: true,
      [PARKING.FIELD_NAME.IS_INCLUDED]: false,
      [PARKING.FIELD_NAME.IS_ADDITIONAL_FEE]: true,
    });
  }

  function onFeeAmountChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [PARKING.FIELD_NAME.ADDITIONAL_FEE_AMOUNT]: value });
  }

  function onAnySpotClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT]: true,
      [PARKING.FIELD_NAME.PROVIDED_IS_RESERVED]: false,
      [PARKING.FIELD_NAME.PROVIDED_RESERVED_SPACE]: '',
    });
  }

  function onReservedSpotClick(): void {
    setSectionValues({
      [PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT]: false,
      [PARKING.FIELD_NAME.PROVIDED_IS_RESERVED]: true,
    });
  }

  function onReservedSpotChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT]: false,
      [PARKING.FIELD_NAME.PROVIDED_IS_RESERVED]: true,
      [PARKING.FIELD_NAME.PROVIDED_RESERVED_SPACE]: value,
    });
  }

  function onVehicleCountChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    setSectionValues({
      [PARKING.FIELD_NAME.ADDITIONAL_FEE_HOW_MANY_VEHICLES]: Number(event.target.value),
    });
  }

  const isNone = section.getFieldValue(PARKING.FIELD_NAME.IS_NONE);
  const isIncluded = section.getFieldValue(PARKING.FIELD_NAME.IS_INCLUDED);
  const isAdditionalFee = section.getFieldValue(PARKING.FIELD_NAME.IS_ADDITIONAL_FEE);

  return (
    <>
      <FormField
        required
        label={<Label>Will the tenant be parking on the property?</Label>}
        control={
          <ButtonGroup
            orientation="vertical"
            conjoined
            css={{ marginBottom: 'layout.tight' }}
            aria-label="tenant-parking"
          >
            <ToggleButton onClick={onNoneClick} selected={isNone}>
              No, none is available
            </ToggleButton>
            <ToggleButton onClick={onIncludedClick} selected={isIncluded}>
              Yes, included in rent
            </ToggleButton>
            <ToggleButton onClick={onAdditionalFeeClick} selected={isAdditionalFee}>
              Yes, for an additional fee
            </ToggleButton>
          </ButtonGroup>
        }
      />
      {isAdditionalFee && (
        <>
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>What is the total parking fee?</Label>}
            control={
              <CurrencyInput
                value={section.getFieldValue(PARKING.FIELD_NAME.ADDITIONAL_FEE_AMOUNT)}
                onChange={onFeeAmountChange}
                onBlur={onFeeAmountChange}
                endAdornment="per month"
              />
            }
          />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>How many vehicles may be parked?</Label>}
            control={
              <Select
                value={String(section.getFieldValue(PARKING.FIELD_NAME.ADDITIONAL_FEE_HOW_MANY_VEHICLES))}
                onChange={onVehicleCountChange}
              >
                {vehicleCountOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            }
          />
        </>
      )}
      {(isIncluded || isAdditionalFee) && (
        <>
          <FieldSet required legend={<Legend>Where may the tenant park?</Legend>}>
            <LabeledControl
              controlId={anyOpenSpotId}
              label={<Label>Any open spot</Label>}
              control={
                <Radio
                  name={radioGroupName}
                  checked={section.getFieldValue(PARKING.FIELD_NAME.PROVIDED_IS_ANY_OPEN_SPOT)}
                  onChange={onAnySpotClick}
                />
              }
            />
            <LabeledControl
              controlId={reservedSpaceId}
              label={<Label>Only in reserved spaces</Label>}
              control={
                <Radio
                  name={radioGroupName}
                  checked={section.getFieldValue(PARKING.FIELD_NAME.PROVIDED_IS_RESERVED)}
                  onChange={onReservedSpotClick}
                />
              }
            />
            <FormField
              required
              css={{ marginLeft: 'lg' }}
              control={
                <Input
                  aria-label="reserved-spaces"
                  onChange={onReservedSpotChange}
                  value={section.getFieldValue(PARKING.FIELD_NAME.PROVIDED_RESERVED_SPACE)}
                  onClick={onReservedSpotClick}
                />
              }
              description={<FormHelp>List the spaces for all vehicles</FormHelp>}
            />
          </FieldSet>
        </>
      )}
    </>
  );
}
